import React from 'react';
import { Placeholder } from 'react-bootstrap';
import { numberFormatter } from '../../../../../utils/helpers/numberFormater';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import Flag from '../../../../../shared/components/flag/Flag';
import { FiInfo } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';

interface TopCountry {
    country: string;
}

interface ListTotals {
    listCount?: number;
    totalFollowers?: number;
    totalAverageViews?: number;
    avgEngagementRate?: number;
    impressionsFrom?: number;
    impressionsTo?: number;
    topCountries?: TopCountry[];
}

interface Props {
    listTotals?: ListTotals;
    isLoading?: boolean;
}

const SKELETON_LABEL_WIDTHS = [70, 64, 170, 165, 80, 150];

const DiscoveryDetailKPISkeleton = () => (
    <div className="d-flex flex-wrap" style={{ gap: 16 }} aria-hidden="true">
        {SKELETON_LABEL_WIDTHS.map((width, i) => {
            const isCountries = i === SKELETON_LABEL_WIDTHS.length - 1;
            return (
                <Placeholder
                    key={i}
                    as="div"
                    animation="glow"
                    className="border default-radius p-2 d-flex flex-column align-items-center"
                >
                    {isCountries ? (
                        <div className="d-flex align-items-center mt-2 mb-1" style={{ gap: 12 }}>
                            {Array.from({ length: 3 }).map((_, j) => (
                                <Placeholder key={j} className="skeleton skeleton-flag" />
                            ))}
                        </div>
                    ) : (
                        <Placeholder className="skeleton skeleton-stat-value mt-2 mb-1" />
                    )}
                    <Placeholder className="skeleton skeleton-stat-label" style={{ width }} />
                </Placeholder>
            );
        })}
    </div>
);

const DiscoveryDetailKPI: React.FC<Props> = ({ listTotals, isLoading = false }) => {
    const { t } = useTranslation();

    if (isLoading) {
        return <DiscoveryDetailKPISkeleton />;
    }

    return (
        <div className="d-flex flex-wrap" style={{ gap: 16 }}>
            <StatCard value={listTotals?.listCount ?? 0} label="Influencers" />

            <StatCard value={numberFormatter(listTotals?.totalFollowers ?? 0)} label="Followers" />

            <StatCard value={numberFormatter(listTotals?.totalAverageViews ?? 0)} label="Total Average Plays/Views" />

            <StatCard value={`${priceSplitter(listTotals?.avgEngagementRate ?? 0)}%`} label="Average Engagement rate" />

            <div className="border default-radius p-2 text-center">
                <div className="d-flex align-items-center justify-content-center">
                    <h6 className="text-primary mb-1 mt-2">
                        {numberFormatter(listTotals?.impressionsFrom ?? 0)} -{' '}
                        {numberFormatter(listTotals?.impressionsTo ?? 0)}
                    </h6>
                    <TooltipOverlay className="ms-1" content={t(`brand.campaign.discovery.impression_tooltip`)}>
                        <FiInfo size={14} />
                    </TooltipOverlay>
                </div>
                <p className="mb-0">Impressions</p>
            </div>

            <div className="border default-radius p-2 text-center">
                <h6 className={`text-primary mb-1 ${!listTotals?.topCountries?.length ? 'mt-2' : ''}`}>
                    {listTotals?.topCountries && listTotals.topCountries.length > 0 ? (
                        <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
                            {listTotals.topCountries.map(({ country }, index) => {
                                const rank =
                                    index === 0 ? '1st' : index === 1 ? '2nd' : index === 2 ? '3rd' : `${index + 1}th`;
                                return (
                                    <div key={country} className="d-flex align-items-center gap-1">
                                        <Flag code={country} width={24} className="rounded-circle" size="1x1" />
                                        <h6 className="text-primary m-1">{rank}</h6>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        'N/A'
                    )}
                </h6>
                <p className="mb-0">{t(`brand.campaign.discovery.top_audience_countries`)}</p>
            </div>
        </div>
    );
};

const StatCard: React.FC<{ value: React.ReactNode; label: string }> = ({ value, label }) => (
    <div className="border default-radius p-2 text-center">
        <h6 className="text-primary mb-1 mt-2">{value}</h6>
        <p className="mb-0">{label}</p>
    </div>
);

export default DiscoveryDetailKPI;
